import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';
import {
  canAccessMenuItem,
  getAllowedMenuItems,
} from './menuPermissionsHelper';

// Map route names to their corresponding menu keys
const ROUTE_TO_MENU_KEY_MAP = {
  // Conversations
  home: 'conversations',
  inbox_dashboard: 'conversations',
  inbox_conversation: 'conversations',
  conversation_through_inbox: 'conversations',
  label_conversations: 'conversations',
  conversations_through_label: 'conversations',
  team_conversations: 'conversations',
  conversations_through_team: 'conversations',
  conversation_mentions: 'conversations',
  conversation_through_mentions: 'conversations',
  conversation_participating: 'conversations',
  conversation_through_participating: 'conversations',
  folder_conversations: 'conversations',
  conversations_through_folders: 'conversations',
  conversation_unread: 'conversations',
  conversation_through_unread: 'conversations',
  conversation_agent_handled: 'conversations',
  conversation_through_agent_handled: 'conversations',
  conversation_not_handled: 'conversations',
  conversation_through_not_handled: 'conversations',

  // Inbox View
  inbox_view: 'inboxView',
  inbox_view_conversation: 'inboxView',

  // Captain
  captain: 'captain',

  // Contacts
  contacts_dashboard: 'contacts',
  contact_profile_dashboard: 'contacts',

  // Reports
  account_overview_reports: 'reports',
  csat_reports: 'reports',
  agent_reports: 'reports',
  label_reports: 'reports',
  inbox_reports: 'reports',
  team_reports: 'reports',

  // Campaigns
  ongoing_campaigns: 'campaigns',
  one_off: 'campaigns',
  whatsapp_campaigns: 'campaigns',
  whatsapp_campaigns_details: 'campaigns',

  // Settings
  settings_home: 'settings',
  agent_list: 'settings',
  canned_list: 'settings',
  label_list: 'settings',
  settings_inbox: 'settings',
  settings_inbox_new: 'settings',
  settings_inbox_list: 'settings',
  settings_inbox_show: 'settings',
  settings_inboxes_page_channel: 'settings',
  settings_inboxes_add_agents: 'settings',
  settings_inbox_finish: 'settings',
  settings_integrations: 'settings',
  settings_integrations_webhook: 'settings',
  settings_integrations_mcp: 'settings',
  settings_integrations_integration: 'settings',
  settings_applications: 'settings',
  settings_applications_integration: 'settings',
  general_settings: 'settings',
  general_settings_index: 'settings',
  settings_teams_list: 'settings',
  settings_teams_new: 'settings',
  settings_teams_add_agents: 'settings',
  settings_teams_finish: 'settings',
  settings_teams_edit: 'settings',
  settings_teams_edit_members: 'settings',
  settings_teams_edit_finish: 'settings',
  attributes_list: 'settings',
  settings_automations: 'settings',
  automation_list: 'settings',
  settings_automation_new: 'settings',
  settings_automation_edit: 'settings',
  macros_wrapper: 'settings',
  macros_edit: 'settings',
  macros_new: 'settings',
  sla_list: 'settings',
  sla_new: 'settings',
  sla_edit: 'settings',

  // Knowledge
  knowledge: 'knowledge',

  // Google Sheet
  google_sheet: 'google_sheet',

  // Knowledge Improvement
  knowledge_improvement: 'knowledge_improvement',

  // Appointments
  appointments: 'appointments',

  // Branches
  branches: 'branches',

  // Rental Branches
  rental_branches: 'rental_branches',

  // Rental Bookings
  rental_bookings: 'rental_bookings',

  // Whitelist
  whitelist: 'whitelist',

  // Condo Management
  condo_management: 'condo_management',
  condo_details: 'condo_management',
  eVoucher: 'eVoucher',
};

export const routeIsAccessibleFor = (
  route,
  userPermissions = [],
  user = null,
  accountId = null,
  teams = []
) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;

  // Check if user has granular menu control enabled
  // Uses helper that handles precedence: agent > team > null (role-based)
  if (user && accountId) {
    const allowedMenuItems = getAllowedMenuItems(user, accountId, teams);
    const useGranularControl = allowedMenuItems !== null;

    if (useGranularControl) {
      // In granular control mode, check if the route's menu key is in allowed_menu_items
      const menuKey = ROUTE_TO_MENU_KEY_MAP[route.name];
      if (menuKey) {
        const canAccess = canAccessMenuItem(menuKey, user, accountId, teams);
        return canAccess;
      }
    }
  }

  // Fall back to role-based permissions
  return hasPermissions(routePermissions, userPermissions);
};

const validateActiveAccountRoutes = (to, user, getters = null) => {
  const accountId = to.params.accountId;
  const userPermissions = getUserPermissions(user, accountId);
  const isServiceAgent = userPermissions.includes('service_agent');

  // If the user is trying to access suspended route, redirect them appropriately
  if (to.name === 'account_suspended') {
    return isServiceAgent
      ? `accounts/${accountId}/appointments`
      : `accounts/${accountId}/dashboard`;
  }

  // Get teams from getters for granular permission checking
  const teams = getters?.['teams/getMyTeams'] || [];

  const isAccessible = routeIsAccessibleFor(
    to,
    userPermissions,
    user,
    accountId,
    teams
  );

  // If the route is not accessible, redirect to appropriate dashboard
  if (!isAccessible) {
    return isServiceAgent
      ? `accounts/${accountId}/appointments`
      : `accounts/${accountId}/dashboard`;
  }

  return null;
};

export const validateLoggedInRoutes = (to, user, getters = null) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user, getters);
  }

  // If the current account is not active, then redirect the user to the suspended screen
  if (to.name !== 'account_suspended') {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unread',
    'conversation_agent_handled',
    'conversation_not_handled',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unread',
    'conversation_through_agent_handled',
    'conversation_through_not_handled',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unread':
      return 'conversation_unread';
    case 'conversation_through_agent_handled':
      return 'conversation_agent_handled';
    case 'conversation_through_not_handled':
      return 'conversation_not_handled';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';
